import {
  type GPSCoordinate,
  type TalkIntakeFilters,
  type Tag,
  type User,
} from '../../shared/types';
import { renderLocationSection, renderTalkAudience } from './location-settings';
import { getDistanceUnit } from './distance-units';
import { normalizeProfileAttributeVisibility } from '../../shared/profile-privacy';
import { TECHSUPPORT_ROOT_USER_ID } from '../../shared/techsupport';
import { escapeHtml } from './ui-formatters';
import { deviceHasGps, getEncounterLocation, isGpsFixUnavailable, usesSelfDefinedEncounterLocation } from '../services/encounter-location';
import {
  discoveryProviderAvailability,
  type DiscoveryProviderAvailability,
  type DiscoveryProviderEnvironment,
  type DiscoveryReadiness,
} from '../../shared/nearby-discovery-policy';
import { renderSettingsSection } from './settings-section-template';
import { hasAppPermissionsBridge, renderAppPermissionsBody } from './app-permissions-settings';
import { loadConnectivitySettings, type ConnectivityPreset } from './connectivity-settings';
import {
  COLOR_SCHEMES,
  getAutoAnswerScope,
  getChatbotEnabled,
  getColorSchemePreference,
  getCopyTalkAutoSave,
  getMatchCollectionEnabled,
  getDefaultTalkLanguagePreference,
  getKeepOldTalkOnEdit,
  getLocationAutoMatchConsent,
  type ColorScheme,
  getNearbyProviderEnabled,
  type NearbyProviderSetting,
} from './ui-settings-storage';
import { renderDownloadAppSectionBody, type AppDownloadTextDeps } from './app-download';
import { renderTechSupportFaqHelpList } from './techsupport-faq-help-view';
import { readNativeHostInfo } from './native-host-info';
import { avatarInnerHtml } from './profile-avatar';
import { languageOptionLabel, type UiLanguage, type UiTranslationKey } from './ui-translations';
import {
  filterIncomingTalkClusters,
  getTalkIntakeFilters,
  getTalkIntakeFiltersOwner,
  hasStoredTalkIntakeFilters,
  setTalkIntakeFilters,
  setTalkIntakeFiltersOwner,
} from './talk-intake-filters';
import {
  normalizeCustomBlockedTerms,
  normalizeDirtyWords,
  DEFAULT_DIRTY_WORDS,
} from '../../shared/talk-intake-filters';
import { CONFIG } from '../../shared/config';
import type { CustomChatroomRow } from './chatrooms-view';
import { roomCreationCredit } from '../../shared/room-creation-credit';
import { CONTACTS_CAP_OPTIONS, getContactsCap } from '../services/contacts-prune';

export interface SettingsViewDeps {
  currentLocation: GPSCoordinate | undefined;
  /** The user's own bottom-layer tile (confirmed GPS only). */
  ownTileId?: string | undefined;
  currentChatroomId?: string;
  incomingTalkClusters: any[];
  customChatrooms: CustomChatroomRow[];
  getHomeChatroomId: () => string;
  formatReasonCounts: (counts: Record<string, number>) => string;
  getUiLanguage: () => UiLanguage;
  formatTalkLanguage: (code: string) => string;
  t: (key: UiTranslationKey) => string;
  tf: (key: UiTranslationKey, values: Record<string, string | number>) => string;
  appDownloadTextDeps: () => AppDownloadTextDeps;
  techSupportDelegateEligible: boolean;
  techSupportDelegateOptedIn: boolean;
  bindSettingsControls: () => void;
  refreshStorageInspector: () => Promise<void>;
  refreshDownloadAppSection: () => Promise<void>;
  renderSupportInboxSectionIfPresent: () => void;
  renderSupportDelegatesSectionIfPresent: () => void;
  renderSupportDelegateOptInSectionIfPresent: () => void;
  renderTechSupportRecoveryBannerIfPresent: () => void;
  applySettingsSectionView: (sectionId: string | null) => void;
  settingsActiveSectionId: string | null;
}

const TALK_TYPE_VALUES: TalkIntakeFilters['allowedTalkTypes'] = ['flow', 'survey', 'tag', 'route'];

const SETTINGS_SCHEME_SWATCHES: Record<ColorScheme, string> = {
  goldenHour: '#cc6b1c',
  tropicalForest: '#2f7a4f',
  snowMountain: '#1f8fc4',
  beachSunset: '#e8637a',
};

const SETTINGS_SCHEME_LABEL_KEYS: Record<ColorScheme, UiTranslationKey> = {
  goldenHour: 'schemeGoldenHour',
  tropicalForest: 'schemeTropicalForest',
  snowMountain: 'schemeSnowMountain',
  beachSunset: 'schemeBeachSunset',
};

export const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: 'Chinese' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
];

export function normalizeStringList(value: unknown, fallback: string[] = []): string[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const normalized = raw.map((part) => String(part || '').trim()).filter(Boolean);
  return normalized.length > 0 ? normalized : fallback;
}

export function normalizeTalkFilterShape(
  value: unknown,
  fallbackLanguages: string[] = ['en'],
): TalkIntakeFilters {
  const stored = value && typeof value === 'object' ? (value as Partial<TalkIntakeFilters>) : {};
  const defaults = getTalkIntakeFilters();
  const allowedTalkTypes = Array.isArray(stored.allowedTalkTypes)
    ? stored.allowedTalkTypes.filter(
        (type): type is TalkIntakeFilters['allowedTalkTypes'][number] =>
          TALK_TYPE_VALUES.includes(type as TalkIntakeFilters['allowedTalkTypes'][number]),
      )
    : [];

  return {
    ...defaults,
    ...stored,
    allowedLanguages: normalizeStringList(stored.allowedLanguages, fallbackLanguages).map((lang) =>
      lang.toLowerCase(),
    ),
    allowedTalkTypes: allowedTalkTypes.length > 0 ? allowedTalkTypes : TALK_TYPE_VALUES,
    customBlockedTerms: normalizeCustomBlockedTerms(
      normalizeStringList(stored.customBlockedTerms, []),
    ),
    dirtyWords:
      stored.dirtyWords === undefined
        ? [...DEFAULT_DIRTY_WORDS]
        : normalizeDirtyWords(stored.dirtyWords),
  };
}

function datetimeLocalValue(value: string | undefined): string {
  if (!value) return '';
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return '';
  const localTimestamp = new Date(timestamp.getTime() - timestamp.getTimezoneOffset() * 60_000);
  return localTimestamp.toISOString().slice(0, 16);
}

export function renderSettingsView(user: User, deps: SettingsViewDeps): void {
  const connectivity = loadConnectivitySettings();
  const { version: appVersion, platform: appPlatform } = readNativeHostInfo();
  const container = document.getElementById('settings-content');
  if (!container) return;
  // adoptSessionUser can now trigger this render as soon as this device's local Gun read
  // for the user record resolves (see app.ts's initializeUser) — before the rest of boot has
  // had a chance to let Gun finish syncing every field in, that local read can momentarily
  // come back missing stageName. Must not assume it's always a non-empty string here.
  const stageNameSafe = user.stageName || '';
  const currentColorScheme = getColorSchemePreference();
  const profileLanguages = normalizeStringList(user.languages, ['en']).map((lang) =>
    lang.toLowerCase(),
  );
  user.languages = profileLanguages;
  // Intake filters are persisted synchronously to localStorage on every settings change
  // (setTalkIntakeFilters, called from ui-manager's sync()), while user.talkFilters comes
  // from an async, eventually-consistent server round-trip (WebUserService.updateTalkFilters
  // queues one getUser+put per change via withPrivateDataLock) that can be caught mid-drain —
  // e.g. by a page reload — and return a partially-applied intermediate state that isn't the
  // hardcoded default shape but also isn't the true latest value (see: e2e
  // 31-intake-filters-persist regression, 2026-08-09, where this silently clobbered the
  // correct localStorage value on reload). localStorage for THIS device is never subject to
  // that race, so prefer it — but ONLY when it was actually saved for the user being rendered
  // now: a stored value tagged for a *different* user id (a device previously used by someone
  // else, or an identity swap) must not be applied to this one, so user.talkFilters wins then.
  const hasUserFilters =
    user.talkFilters &&
    typeof user.talkFilters === 'object' &&
    Object.keys(user.talkFilters).length > 0;
  const normalizedUserFilters = hasUserFilters
    ? normalizeTalkFilterShape(user.talkFilters, profileLanguages)
    : null;
  const localStorageOwnedByThisUser =
    hasStoredTalkIntakeFilters() && getTalkIntakeFiltersOwner() === user.id;
  const talkFilters = localStorageOwnedByThisUser
    ? getTalkIntakeFilters()
    : (normalizedUserFilters ?? normalizeTalkFilterShape(undefined, profileLanguages));
  user.talkFilters = talkFilters;
  setTalkIntakeFilters(talkFilters);
  setTalkIntakeFiltersOwner(user.id);
  const reputation = user.reputation || ({} as typeof user.reputation);
  const reviewCount = reputation.reviewCount ?? 0;
  const starRating = Number(reputation.starRating ?? 0);
  const friendsCount = reputation.friendsCount ?? 0;
  const blockCount = reputation.blockCount ?? 0;
  const creationCredit = roomCreationCredit(reputation, undefined, {
    minFriendMarks: CONFIG.PLACE_CREATE_MIN_FRIEND_MARKS,
    maxBlocksExclusive: CONFIG.PLACE_CREATE_MAX_BLOCKS_EXCLUSIVE,
  });
  const matchesFound = reputation.matchesFound ?? 0;
  const likedCount = reputation.likedCount ?? 0;
  const dislikedCount = reputation.dislikedCount ?? 0;
  const ageVerified = reputation.ageVerified === true;
  const isCreditVisible = reputation.isHidden !== true;
  const headshot = String(user.headshot || '').trim();
  const interestNames = Array.isArray(user.interests)
    ? user.interests.map((t: Tag) => String(t?.name || '').trim()).filter(Boolean)
    : [];
  const profileAnswers = Array.isArray(user.profile) ? user.profile : [];
  const profilePreview =
    profileAnswers.length > 0
      ? profileAnswers
          .slice(0, 4)
          .map((qa) => {
            const vis = normalizeProfileAttributeVisibility(qa.visibility);
            const canonicalSupportRole =
              qa.id === 'techsupport_profile_role' &&
              qa.question === 'Role' &&
              qa.answer === 'IinPublic network support';
            const visNote =
              vis === 'public'
                ? ''
                : `<div style="font-size:0.72em;color:var(--text-tertiary);margin-top:2px;">${escapeHtml(
                    vis === 'contacts_only'
                      ? deps.t('meVisibilityContacts')
                      : deps.t('meVisibilityPrivate'),
                  )}</div>`;
            const question = canonicalSupportRole ? deps.t('meTechSupportRole') : qa.question;
            const answer = canonicalSupportRole ? deps.t('meTechSupportRoleValue') : qa.answer;
            return `<div style="padding:8px 10px;border-radius:8px;background:var(--bg-subtle);border:1px solid var(--border);"><div style="font-size:0.78em;color:var(--text-tertiary);">${escapeHtml(question)}</div>${visNote}<div style="font-size:0.92em;font-weight:600;color:var(--text-primary);margin-top:2px;">${escapeHtml(answer)}</div></div>`;
          })
          .join('')
      : `<div style="font-size:0.88em;color:var(--text-tertiary);">${escapeHtml(deps.t('meNoPublicProfile'))}</div>`;
  // Never echo a GPS coordinate in Settings. The person manages the disclosed *area*, while the
  // exact fix remains an implementation detail on this device.
  const locationText = deps.currentLocation
    ? 'Location available · exact GPS stays on this device'
    : 'Location unavailable · automatic Nearby is inactive';
  const filteredIncoming = filterIncomingTalkClusters(
    (deps.incomingTalkClusters || []).filter((cluster: any) => cluster && cluster.identityKey),
    talkFilters,
    deps.currentLocation,
  );
  const hiddenIncomingText = deps.formatReasonCounts(filteredIncoming.hiddenByReason);
  const dirtyWordList =
    talkFilters.dirtyWords === undefined
      ? [...DEFAULT_DIRTY_WORDS]
      : normalizeDirtyWords(talkFilters.dirtyWords);
  const uiLanguage = deps.getUiLanguage();
  const defaultTalkLanguage = getDefaultTalkLanguagePreference(uiLanguage);
  const languageOptions = LANGUAGE_OPTIONS.map((language) => ({
    ...language,
    label: languageOptionLabel(uiLanguage, language.code, language.label),
  }));
  const headshotChoices = ['🙂', '😎', '🤠', '🎾', '☕', '🌟', '🐱', '🦊'];
  // Drill-down menu: related destinations are grouped so the settings landing page scans like a
  // short outline instead of one long list. Profile and Help & Tour deliberately remain the first
  // and last destinations. Tapping any row still hides the menu and every OTHER section, leaving
  // just the selected section + a back button (app-bar #back-to-settings-menu). Sections remain
  // rendered up front so their ids stay stable across section switches.
  type JumpMenuItem = { icon: string; label: string; target: string };
  const jumpMenuGroups: Array<{ label?: string; items: JumpMenuItem[] }> = [
    {
      items: [{ icon: '👤', label: deps.t('profile'), target: 'settings-section-profile' }],
    },
    {
      label: deps.t('settingsGroupPersonalization'),
      items: [
        { icon: '🎨', label: deps.t('settingsAppearance'), target: 'settings-section-appearance' },
        { icon: '🌐', label: deps.t('settingsLanguages'), target: 'settings-section-languages' },
      ],
    },
    {
      label: deps.t('settingsGroupTalksDiscovery'),
      items: [
        { icon: '🗣️', label: deps.t('settingsTalkBehavior'), target: 'settings-section-talk-behavior' },
        { icon: '🚫', label: deps.t('settingsContentFilters'), target: 'settings-section-content-filters' },
        { icon: '📍', label: deps.t('settingsDistanceHome'), target: 'settings-section-distance-home' },
      ],
    },
    {
      label: deps.t('settingsGroupIdentityTrust'),
      items: [
        { icon: '⭐', label: deps.t('credit'), target: 'settings-section-credit' },
        { icon: '🔐', label: deps.t('settingsIdentityDevices'), target: 'settings-section-linked-devices' },
      ],
    },
    {
      label: deps.t('settingsGroupAppData'),
      items: [
        ...(hasNearbyBridge() ? [{ icon: '📶', label: deps.t('settingsNearby'), target: 'settings-section-nearby' }] : []),
        { icon: '📡', label: 'Connectivity', target: 'settings-section-connectivity' },
        ...(hasAppPermissionsBridge() ? [{ icon: '🔐', label: deps.t('appPermissionsTitle'), target: 'settings-section-app-permissions' }] : []),
        { icon: '📱', label: deps.t('settingsDownloadApp'), target: 'settings-section-download-app' },
        { icon: '💾', label: deps.t('settingsStorage'), target: 'settings-storage-inspector' },
        { icon: '🗑️', label: deps.t('settingsEraseDevice'), target: 'settings-section-erase-device' },
      ],
    },
    ...(user.id === TECHSUPPORT_ROOT_USER_ID
      ? []
      : [{
          label: deps.t('settingsGroupSupport'),
          items: [{ icon: '🛟', label: deps.t('supportDelegateInviteEntryTitle'), target: 'settings-section-support-delegate' }],
        }]),
    {
      items: [{ icon: '❓', label: deps.t('settingsHelp'), target: 'settings-section-help' }],
    },
  ];
  const jumpMenuHtml = `
      <div data-testid="settings-product-promise" style="padding:2px 4px;font-size:0.92em;font-weight:650;color:var(--text-secondary);">
        ${deps.t('settingsOwnershipPromise')}
      </div>
      <div class="settings-jump-menu" id="settings-jump-menu">
        ${jumpMenuGroups
          .map(
            (group) => `
          <section class="settings-jump-menu-group" ${group.label ? `aria-label="${escapeHtml(group.label)}"` : ''}>
            ${group.label ? `<h3 class="settings-jump-menu-group-label">${escapeHtml(group.label)}</h3>` : ''}
            <div class="settings-jump-menu-group-items">
              ${group.items
                .map(
                  (item, index) => `
                <button type="button" class="settings-jump-menu-item" data-target="${item.target}" style="${index < group.items.length - 1 ? 'border-bottom:1px solid var(--border);' : ''}">
                  <span aria-hidden="true" class="settings-jump-menu-icon">${item.icon}</span>
                  <span class="settings-jump-menu-label">${item.label}</span>
                  <span aria-hidden="true" class="settings-jump-menu-chevron">›</span>
                </button>
              `,
                )
                .join('')}
            </div>
          </section>
        `,
          )
          .join('')}
      </div>
      <div id="settings-app-version" data-testid="settings-app-version" style="padding:2px 4px;text-align:center;font-size:0.78em;color:var(--text-tertiary);">
        IinPublic version ${escapeHtml(appVersion)}${appPlatform ? ` · ${escapeHtml(appPlatform)}` : ''}
      </div>
    `;
  container.innerHTML = `
      <div id="settings-menu-container" data-testid="settings-menu-container" style="display:grid;gap:14px;">
        ${jumpMenuHtml}
      </div>
      <div id="settings-detail-container" data-testid="settings-detail-container" style="display:none;">
        <div style="display:grid;gap:14px;">
        ${renderSettingsSection(
          { id: 'settings-section-profile', title: deps.t('profile') },
          `
          <div style="display:grid;grid-template-columns:minmax(0,1fr);gap:14px;align-items:start;">
            <div style="display:grid;gap:8px;min-width:0;">
              <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;">
                <span>${deps.t('settingsStageName')}</span>
                <input type="text" class="form-input" id="settings-stage-name-input" data-testid="settings-stage-name-input" value="${escapeHtml(stageNameSafe)}" minlength="3">
              </label>
              <div id="settings-stage-name-error" role="alert" style="display:none;font-size:0.82em;color:var(--danger-hover);margin-top:5px;"></div>
              ${interestNames.length > 0 ? `<div style="font-size:0.86em;color:var(--text-tertiary);">${deps.t('interestsLabel')}: ${escapeHtml(interestNames.join(', '))}</div>` : ''}
            </div>
            <div style="display:grid;gap:8px;font-size:0.9em;">
              <span>${deps.t('settingsHeadshot')}</span>
              <div class="user-avatar" style="width:72px;height:72px;font-size:1.7em;">
                ${avatarInnerHtml(headshot, stageNameSafe.charAt(0).toUpperCase() || '?', escapeHtml)}
              </div>
              <select class="form-input" id="settings-headshot-select" data-testid="settings-headshot-select">
                <option value="">${deps.t('settingsInitial')}</option>
                ${headshotChoices
                  .map(
                    (choice) =>
                      `<option value="${choice}" ${choice === headshot ? 'selected' : ''}>${choice}</option>`,
                  )
                  .join('')}
              </select>
              <div style="display:flex;gap:6px;flex-wrap:wrap;">
                <button class="btn" type="button" id="settings-choose-photo-btn">${deps.t('settingsChoosePhoto')}</button>
                <button class="btn" type="button" id="settings-take-photo-btn">${deps.t('settingsTakePhoto')}</button>
                <button class="btn" type="button" id="settings-remove-photo-btn">${deps.t('settingsRemove')}</button>
              </div>
              <input class="visually-hidden" type="file" id="settings-photo-input" accept="image/*">
              <input class="visually-hidden" type="file" id="settings-camera-input" accept="image/*" capture="user">
              <div style="font-size:0.78em;color:var(--text-tertiary);">${deps.t('settingsPhotoHelp')}</div>
              <div id="settings-camera-status" role="status" style="display:none;font-size:0.8em;color:var(--danger-hover);"></div>
            </div>
            <div style="display:grid;gap:10px;border-top:1px solid var(--border);padding-top:12px;">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;">
                <div>
                  <div style="font-weight:700;color:var(--text-primary);">${deps.t('profile')}</div>
                  <div style="font-size:0.82em;color:var(--text-tertiary);">${deps.t('meProfileVisibilityHelp')}</div>
                </div>
                <button class="btn" type="button" id="settings-edit-profile-btn" data-testid="settings-edit-profile-button">${deps.t('editProfile')}</button>
              </div>
              <div style="font-size:0.88em;color:var(--text-secondary);">
                ${deps.t('languagesLabel')}: ${escapeHtml(profileLanguages.map((code) => deps.formatTalkLanguage(code)).join(', '))}
              </div>
              <div style="display:grid;gap:8px;">${profilePreview}</div>
            </div>
          </div>
        `,
        )}
        ${renderSettingsSection(
          {
            id: 'settings-section-appearance',
            title: deps.t('settingsAppearance'),
            subtitle: deps.t('settingsAppearanceHelp'),
          },
          `
          <div id="settings-scheme-picker" style="display:grid;gap:8px;">
            ${COLOR_SCHEMES.map((scheme) => {
              const swatch = SETTINGS_SCHEME_SWATCHES[scheme];
              const checked = currentColorScheme === scheme;
              return `
                <label class="settings-scheme-option" data-scheme="${scheme}" style="display:flex;align-items:center;gap:12px;padding:10px 12px;border:1.5px solid ${checked ? 'var(--accent)' : 'var(--border)'};border-radius:10px;cursor:pointer;">
                  <input type="radio" name="settings-color-scheme" class="settings-scheme-radio" value="${scheme}" ${checked ? 'checked' : ''}>
                  <span style="width:26px;height:26px;border-radius:50%;flex:none;background:${swatch};border:1px solid rgba(0,0,0,0.08);"></span>
                  <span style="font-weight:600;font-size:0.92em;">${deps.t(SETTINGS_SCHEME_LABEL_KEYS[scheme])}</span>
                </label>
              `;
            }).join('')}
          </div>
          <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;margin-top:12px;">
            <span>${deps.t('settingsDistanceUnit')}</span>
            <select class="form-input" id="settings-distance-unit" data-testid="settings-distance-unit">
              <option value="km" ${getDistanceUnit() === 'km' ? 'selected' : ''}>${deps.t('settingsDistanceUnitKm')}</option>
              <option value="mi" ${getDistanceUnit() === 'mi' ? 'selected' : ''}>${deps.t('settingsDistanceUnitMi')}</option>
            </select>
          </label>
        `,
        )}
        ${renderSettingsSection(
          {
            id: 'settings-section-credit',
            title: deps.t('credit'),
            subtitle: deps.t('meCreditHelp'),
            action: `
              <label style="display:flex;align-items:center;gap:8px;font-size:0.9em;">
                <input type="checkbox" id="settings-credit-visible" ${isCreditVisible ? 'checked' : ''}>
                <span>${deps.t('settingsCreditVisible')}</span>
              </label>
            `,
          },
          `
          <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;">
            <div style="padding:10px;border-radius:8px;background:var(--warning-soft);border:1px solid var(--warning-border);"><div style="font-size:0.78em;color:var(--warning-text);">${deps.t('meReviews')}</div><div style="font-size:1.15em;font-weight:700;">${reviewCount}</div></div>
            <div style="padding:10px;border-radius:8px;background:var(--warning-soft);border:1px solid var(--warning-border);"><div style="font-size:0.78em;color:var(--warning-text);">${deps.t('meStarRating')}</div><div style="font-size:1.15em;font-weight:700;">${starRating.toFixed(1)}</div></div>
            <div style="padding:10px;border-radius:8px;background:var(--warning-soft);border:1px solid var(--warning-border);"><div style="font-size:0.78em;color:var(--warning-text);">${deps.t('meFriends')}</div><div style="font-size:1.15em;font-weight:700;">${friendsCount}</div></div>
            <div style="padding:10px;border-radius:8px;background:var(--warning-soft);border:1px solid var(--warning-border);"><div style="font-size:0.78em;color:var(--warning-text);">${deps.t('meBlocks')}</div><div style="font-size:1.15em;font-weight:700;">${blockCount}</div></div>
            <div style="padding:10px;border-radius:8px;background:var(--warning-soft);border:1px solid var(--warning-border);"><div style="font-size:0.78em;color:var(--warning-text);">${deps.t('meLiked')}</div><div style="font-size:1.15em;font-weight:700;">${likedCount}</div></div>
            <div style="padding:10px;border-radius:8px;background:var(--warning-soft);border:1px solid var(--warning-border);"><div style="font-size:0.78em;color:var(--warning-text);">${deps.t('meDisliked')}</div><div style="font-size:1.15em;font-weight:700;">${dislikedCount}</div></div>
            <div style="padding:10px;border-radius:8px;background:var(--warning-soft);border:1px solid var(--warning-border);"><div style="font-size:0.78em;color:var(--warning-text);">${deps.t('meMatches')}</div><div style="font-size:1.15em;font-weight:700;">${matchesFound}</div></div>
            <div style="padding:10px;border-radius:8px;background:var(--warning-soft);border:1px solid var(--warning-border);grid-column:span 2;"><div style="font-size:0.78em;color:var(--warning-text);">${deps.t('meAgeVerified')}</div><div style="font-size:1.15em;font-weight:700;">${ageVerified ? '18+' : deps.t('unavailable')}</div></div>
            <div data-testid="room-creation-credit" style="padding:10px;border-radius:8px;background:var(--bg-subtle);border:1px solid var(--border);grid-column:span 2;">
              <div style="font-size:0.78em;color:var(--text-tertiary);">${deps.t('meRoomCreationCredit')}</div>
              <div style="font-size:0.92em;font-weight:650;color:var(--text-primary);">${creationCredit.eligible
                ? deps.t('meRoomCreationUnlocked')
                : deps.tf('meRoomCreationLocked', {
                    requiredFriends: creationCredit.requiredFriendMarks,
                    maximumBlocks: creationCredit.maximumAllowedBlocks,
                    friends: creationCredit.friendMarks,
                    blocks: creationCredit.blockCount,
                  })}</div>
            </div>
          </div>
        `,
        )}
        ${renderSettingsSection(
          { id: 'settings-section-languages', title: deps.t('settingsLanguages') },
          `
          <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;">
            <span>${deps.t('settingsUiLanguage')}</span>
            <select class="form-input" id="settings-ui-language" data-testid="settings-ui-language-select">
              ${languageOptions
                .filter((lang) => lang.code === 'en' || lang.code === 'zh')
                .map(
                  (lang) =>
                    `<option value="${lang.code}" ${uiLanguage === lang.code ? 'selected' : ''}>${lang.label}</option>`,
                )
                .join('')}
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;margin-top:10px;">
            <span>${deps.t('settingsProfileLanguage')}</span>
            <select class="form-input" id="settings-profile-languages" data-testid="settings-profile-language-select">
              ${languageOptions
                .map(
                  (lang) =>
                    `<option value="${lang.code}" ${profileLanguages[0] === lang.code ? 'selected' : ''}>${lang.label}</option>`,
                )
                .join('')}
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;margin-top:10px;">
            <span>${deps.t('settingsDefaultTalkLanguage')}</span>
            <select class="form-input" id="settings-default-talk-language" data-testid="settings-default-talk-language-select">
              ${languageOptions
                .map(
                  (lang) =>
                    `<option value="${lang.code}" ${defaultTalkLanguage === lang.code ? 'selected' : ''}>${lang.label}</option>`,
                )
                .join('')}
            </select>
          </label>
          <div style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;margin-top:10px;">
            <span>${deps.t('settingsIncomingLanguage')}</span>
            <div id="settings-filter-languages" data-testid="settings-incoming-language-select" style="display:flex;flex-wrap:wrap;gap:8px;">
              ${languageOptions
                .map(
                  (lang) => `
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.9em;padding:6px 10px;border:1px solid var(--border-strong);border-radius:999px;background:var(--surface);">
                    <input type="checkbox" class="settings-filter-language-option" value="${lang.code}" ${talkFilters.allowedLanguages.includes(lang.code) ? 'checked' : ''}>
                    <span>${lang.label}</span>
                  </label>
                `,
                )
                .join('')}
            </div>
            <div id="settings-filter-languages-count" style="font-size:0.82em;color:var(--text-tertiary);">${talkFilters.allowedLanguages.length} ${deps.t('settingsActive')}</div>
          </div>
        `,
        )}
        ${renderSettingsSection(
          { id: 'settings-section-talk-behavior', title: deps.t('settingsTalkBehavior') },
          `
          <label style="display:flex;align-items:center;gap:10px;cursor:pointer;font-size:0.95em;">
            <input type="checkbox" id="settings-copy-talk-autosave" ${getCopyTalkAutoSave() ? 'checked' : ''}>
            <span>${deps.t('settingsCopyTalk')}</span>
          </label>
          <label style="display:flex;align-items:center;gap:10px;cursor:pointer;font-size:0.95em;margin-top:12px;">
            <input type="checkbox" id="settings-match-collection-enabled" data-testid="settings-match-collection-enabled" ${getMatchCollectionEnabled() ? 'checked' : ''}>
            <span>${deps.t('settingsMatchCollection')}</span>
          </label>
          <div style="font-size:0.82em;color:var(--text-tertiary);margin:2px 0 0 26px;">${deps.t('settingsMatchCollectionHelp')}</div>
          <label style="display:flex;align-items:center;gap:10px;cursor:pointer;font-size:0.95em;margin-top:12px;">
            <input type="checkbox" id="settings-chatbot-enabled" ${getChatbotEnabled() ? 'checked' : ''}>
            <span>${deps.t('settingsChatbot')}</span>
          </label>
          <div data-testid="settings-chatbot-promise" style="font-size:0.82em;color:var(--text-tertiary);margin:2px 0 0 26px;">${deps.t('settingsChatbotHelp')}</div>
          <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;margin-top:14px;">
            <span>${deps.t('settingsAutoAnswerScope')}</span>
            <select class="form-input" id="settings-auto-answer-scope" data-testid="settings-auto-answer-scope">
              <option value="same-context" ${getAutoAnswerScope() === 'same-context' ? 'selected' : ''}>${deps.t('settingsAutoAnswerSameContext')}</option>
              <option value="whenever" ${getAutoAnswerScope() === 'whenever' ? 'selected' : ''}>${deps.t('settingsAutoAnswerWhenever')}</option>
            </select>
          </label>
          <div style="font-size:0.82em;color:var(--text-tertiary);margin-top:3px;">${deps.t('settingsAutoAnswerScopeHelp')}</div>
          <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;margin-top:14px;">
            <span>${deps.t('settingsContactsCap')}</span>
            <select class="form-input" id="settings-contacts-cap" data-testid="settings-contacts-cap">
              ${CONTACTS_CAP_OPTIONS.map((cap) => `<option value="${cap}" ${getContactsCap() === cap ? 'selected' : ''}>${cap.toLocaleString()}</option>`).join('')}
            </select>
          </label>
          <div style="font-size:0.82em;color:var(--text-tertiary);margin-top:3px;">${deps.t('settingsContactsCapHelp')}</div>
          <label style="display:flex;align-items:center;gap:10px;cursor:pointer;font-size:0.95em;margin-top:12px;">
            <input type="checkbox" id="settings-location-auto-match-consent" ${getLocationAutoMatchConsent() ? 'checked' : ''}>
            <span>${deps.t('settingsLocationAutoMatch')}</span>
          </label>
          <div style="font-size:0.82em;color:var(--text-tertiary);margin:2px 0 0 26px;">${deps.t('settingsLocationAutoMatchNote')}</div>
          <label style="display:flex;align-items:center;gap:10px;cursor:pointer;font-size:0.95em;margin-top:12px;">
            <input type="checkbox" id="settings-keep-old-talk-on-edit" ${getKeepOldTalkOnEdit() ? 'checked' : ''}>
            <span>${deps.t('settingsKeepOldTalkOnEdit')}</span>
          </label>
          ${renderTalkAudience(deps.t, talkFilters)}
          <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;margin-top:10px;">
            <span>${deps.t('settingsSentAfter')}</span>
            <input type="datetime-local" class="form-input" id="settings-sent-after" value="${escapeHtml(datetimeLocalValue(talkFilters.sentAfter))}">
          </label>
        `,
        )}
        ${renderSettingsSection(
          {
            id: 'settings-section-distance-home',
            title: deps.t('locationSectionTitle'),
            subtitle: deps.t('locationSectionHelp'),
          },
          `
          ${renderLocationSection({
            t: deps.t,
            connectivity,
            hasGps: deviceHasGps(),
            traveler: usesSelfDefinedEncounterLocation(),
            gpsFixUnavailable: isGpsFixUnavailable(),
            encounterBlock: renderEncounterLocationBlock(deps.t),
          })}
          <div style="margin-top:8px;font-size:0.82em;color:var(--text-tertiary);">${escapeHtml(locationText)}</div>
        `,
        )}
        ${renderSettingsSection(
          {
            id: 'settings-section-content-filters',
            title: deps.t('settingsContentFilters'),
            subtitle: deps.t('settingsContentFiltersHelp'),
          },
          `
          <div style="font-size:0.85em;font-weight:600;color:var(--text-secondary);margin:16px 0 8px;">${deps.t('settingsMessageFiltersHeading')}</div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;">
            <label style="display:flex;align-items:center;gap:8px;font-size:0.9em;"><input type="checkbox" id="settings-grammar-filter" ${talkFilters.requireGoodGrammar ? 'checked' : ''}> ${deps.t('settingsGrammar')}</label>
            <label style="display:flex;align-items:center;gap:8px;font-size:0.9em;"><input type="checkbox" id="settings-dirty-words-filter" ${talkFilters.blockDirtyWords ? 'checked' : ''}> ${deps.t('settingsDirtyWords')}</label>
          </div>
          <div style="font-size:0.8em;color:var(--text-tertiary);margin-top:8px;">${deps.t('settingsGrammarHelp')} ${deps.tf('settingsGrammarStrictness', { threshold: String(CONFIG.GRAMMAR_THRESHOLD) })}</div>
          <div style="font-size:0.8em;color:var(--text-tertiary);margin-top:4px;">${deps.t('settingsDirtyWordsHelp')}</div>
          <div id="settings-dirty-words-editor" style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border);">
            <div style="font-size:0.9em;font-weight:600;margin-bottom:6px;">${deps.t('settingsDirtyWordsListLabel')}</div>
            <div id="dirty-word-chips" data-testid="dirty-word-chips" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px;">
              ${dirtyWordList
                .map(
                  (word) => `
                  <span class="dirty-word-chip" data-testid="dirty-word-chip" data-word="${escapeHtml(word)}" style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border-strong);border-radius:999px;background:var(--bg-subtle);font-size:0.85em;">
                    <span>${escapeHtml(word)}</span>
                    <button type="button" class="dirty-word-chip-remove" data-testid="dirty-word-chip-remove" data-word="${escapeHtml(word)}" aria-label="remove ${escapeHtml(word)}" style="border:none;background:none;cursor:pointer;color:var(--text-tertiary);font-size:1em;line-height:1;padding:0;">✕</button>
                  </span>
                `,
                )
                .join('')}
            </div>
            <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
              <input type="text" class="form-input" id="dirty-word-add-input" data-testid="dirty-word-add-input" placeholder="${deps.t('settingsDirtyWordAddPlaceholder')}" maxlength="48" style="flex:1;min-width:140px;">
              <button type="button" class="btn" id="dirty-word-add-btn" data-testid="dirty-word-add-btn">${deps.t('settingsDirtyWordAdd')}</button>
              <button type="button" class="btn" id="dirty-word-reset-btn" data-testid="dirty-word-reset-btn">${deps.t('settingsDirtyWordReset')}</button>
            </div>
            <div id="dirty-word-error" data-testid="dirty-word-error" style="font-size:0.8em;color:var(--danger);margin-top:4px;min-height:1em;"></div>
            <div style="font-size:0.8em;color:var(--text-tertiary);margin-top:2px;">${deps.t('settingsDirtyWordsListHelp')}</div>
          </div>
          <div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border);">
            <div style="font-size:0.9em;font-weight:600;margin-bottom:6px;">${deps.t('settingsAllowedTypes')}</div>
            <div style="display:flex;flex-wrap:wrap;gap:8px;">
              ${(['tag', 'flow', 'route', 'survey'] as const)
                .map(
                  (type) => `
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.9em;padding:6px 10px;border:1px solid var(--border-strong);border-radius:999px;background:var(--surface);">
                    <input type="checkbox" class="settings-talk-filter-type" value="${type}" ${talkFilters.allowedTalkTypes.includes(type) ? 'checked' : ''}>
                    <span>${type}</span>
                  </label>
                `,
                )
                .join('')}
            </div>
          </div>
          <div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border);">
            <label style="display:flex;flex-direction:column;gap:6px;font-size:0.9em;">
              <span>${deps.t('settingsBlockedPhrases')}</span>
              <textarea class="form-input" id="settings-custom-blocked" rows="3">${escapeHtml((talkFilters.customBlockedTerms || []).join(', '))}</textarea>
            </label>
          </div>
          <div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border);">
            <div style="font-size:0.9em;font-weight:600;margin-bottom:6px;">${deps.t('settingsFilteredIncomingHeading')}</div>
            <div id="settings-filtered-incoming-summary" style="font-size:0.84em;color:var(--text-tertiary);">
              ${deps.t('settingsHiddenIncoming')}: ${filteredIncoming.hiddenCount}
              ${hiddenIncomingText ? `<div>${escapeHtml(hiddenIncomingText)}</div>` : ''}
              ${!deps.currentLocation && (deps.incomingTalkClusters || []).some((c: any) => c?.latestTalk?.locationRadiusMiles != null || c?.locationRadiusMiles != null) ? `<div style="color:var(--warning-text);font-style:italic;margin-top:4px;">${escapeHtml(deps.t('filterLocationPending'))}</div>` : ''}
            </div>
          </div>
        `,
        )}
        ${hasNearbyBridge() ? renderSettingsSection(
          {
            id: 'settings-section-nearby',
            title: deps.t('settingsNearby'),
            subtitle: deps.t('settingsNearbySubtitle'),
          },
          `<div style="display:grid;gap:10px;" data-testid="settings-nearby">
            <div id="settings-nearby-status" role="status" data-testid="settings-nearby-status" style="font-weight:600;"></div>
            <label>${deps.t('settingsNearbyExchange')}
              <select id="settings-nearby-mode" class="form-input" data-testid="settings-nearby-mode">
                <option value="off" ${connectivity.nearbyMode === 'off' ? 'selected' : ''}>${deps.t('settingsNearbyModeOff')}</option>
                <option value="while-open" ${connectivity.nearbyMode === 'while-open' ? 'selected' : ''}>${deps.t('settingsNearbyModeWhileOpen')}</option>
                <option value="always" ${connectivity.nearbyMode === 'always' ? 'selected' : ''}>${deps.t('settingsNearbyModeAlways')}</option>
              </select>
              <small>${deps.t('settingsNearbyModeHelp')}</small>
            </label>
            ${renderNearbyProviderRow('cellular', 'settingsNearbyCellular', 'settingsNearbyCellularHelp', deps.t)}
            ${renderNearbyProviderRow('lan', 'settingsNearbyLan', 'settingsNearbyLanHelp', deps.t)}
            ${renderNearbyProviderRow('wifi-direct', 'settingsNearbyWifiDirect', 'settingsNearbyWifiDirectHelp', deps.t)}
            ${renderNearbyProviderRow('bluetooth', 'settingsNearbyBluetooth', 'settingsNearbyBluetoothHelp', deps.t)}
            <details><summary>${deps.t('settingsNearbyPrivacyTitle')}</summary>
              <ul style="margin:6px 0 0 18px;padding:0;font-size:0.88em;">
                <li>${deps.t('settingsNearbyPrivacySameWifi')}</li>
                <li>${deps.t('settingsNearbyPrivacyOnlyOffline')}</li>
                <li>${deps.t('settingsNearbyPrivacyEncrypted')}</li>
                <li>${deps.t('settingsNearbyPrivacyVisible')}</li>
                <li>${deps.t('settingsNearbyPrivacyBluetooth')}</li>
              </ul>
            </details>
            <small style="color:var(--text-tertiary);">${deps.t('settingsNearbyScreenOff')}</small>
          </div>`,
        ) : ''}
        ${hasAppPermissionsBridge() ? renderSettingsSection(
          {
            id: 'settings-section-app-permissions',
            title: deps.t('appPermissionsTitle'),
            subtitle: deps.t('appPermissionsSubtitle'),
          },
          renderAppPermissionsBody(deps.t),
        ) : ''}
        ${renderSettingsSection(
          {
            id: 'settings-section-connectivity',
            title: 'Connectivity',
            subtitle: 'Choose routes automatically or tune data, battery, and forwarding policy.',
          },
          `<div style="display:grid;gap:12px;">
            <label>Preset
              <select id="settings-connectivity-preset" class="form-input" data-testid="settings-connectivity-preset">
                ${(['automatic', 'data-saver', 'fastest', 'local-event', 'private', 'advanced'] as ConnectivityPreset[]).map((preset) => `<option value="${preset}" ${connectivity.preset === preset ? 'selected' : ''}>${preset.replace('-', ' ')}</option>`).join('')}
              </select>
            </label>
            <div id="settings-connectivity-status" role="status">Automatic route selection · forwarding ${connectivity.forwarding.enabled ? 'on' : 'off'}</div>
            <label><input id="settings-connectivity-free-first" type="checkbox" ${connectivity.freeFirst ? 'checked' : ''}> Free routes first</label>
            <label><input id="settings-connectivity-direct-first" type="checkbox" ${connectivity.directFirst ? 'checked' : ''}> Direct routes first</label>
            <label><input id="settings-connectivity-battery-aware" type="checkbox" ${connectivity.batteryAware ? 'checked' : ''}> Battery-aware</label>
            <label><input id="settings-connectivity-wifi-forwarding" type="checkbox" ${connectivity.wifiOnlyForwarding ? 'checked' : ''}> Allow peer forwarding over Wi-Fi/local routes</label>
            <label>Identity reveal
              <select id="settings-connectivity-identity-reveal" class="form-input">
                <option value="matches-only" ${connectivity.identityReveal === 'matches-only' ? 'selected' : ''}>Only after a Talk match</option>
                <option value="room-presence" ${connectivity.identityReveal === 'room-presence' ? 'selected' : ''}>Allow room presence identity</option>
              </select>
            </label>
            <label>Metered network permission
              <select id="settings-connectivity-metered-permission" class="form-input">
                ${(['ask', 'allow-once', 'always-allow', 'wait-for-free'] as const).map((permission) => `<option value="${permission}" ${connectivity.meteredPermission === permission ? 'selected' : ''}>${permission.replaceAll('-', ' ')}</option>`).join('')}
              </select>
              <small>IinPublic asks before using a newly metered route. Nearby OS permission enables local discovery; denying it leaves Internet connectivity available.</small>
            </label>
            <label><input id="settings-connectivity-forwarding" type="checkbox" ${connectivity.forwarding.enabled ? 'checked' : ''}> Forward for peers</label>
            <label><input id="settings-connectivity-cellular-forwarding" type="checkbox" ${connectivity.forwarding.cellularForwarding ? 'checked' : ''}> Allow cellular forwarding</label>
            <label>Cellular byte budget <input id="settings-connectivity-cellular-budget" class="form-input" type="number" min="0" value="${connectivity.forwarding.cellularByteBudget}"></label>
            <details><summary>Advanced diagnostics</summary><div id="settings-connectivity-diagnostics" data-testid="settings-connectivity-diagnostics">Providers and verified SEA bindings appear here when active. Transport identifiers are diagnostics only.</div></details>
          </div>`,
        )}
        ${renderSettingsSection(
          {
            id: 'settings-section-download-app',
            title: deps.t('settingsDownloadApp'),
            subtitle: deps.t('settingsDownloadAppSubtitle'),
          },
          `<div id="settings-download-app-body" data-testid="settings-download-app-body" style="display:grid;gap:10px;">${renderDownloadAppSectionBody(deps.appDownloadTextDeps(), {})}</div>`,
        )}
        ${renderSettingsSection(
          {
            id: 'settings-section-linked-devices',
            title: deps.t('settingsIdentityDevices'),
            subtitle: deps.t('settingsIdentityDevicesHelp'),
            action: `<button type="button" class="btn" id="settings-linked-devices-btn" data-testid="settings-linked-devices-btn">${deps.t('settingsManage')}</button>`,
          },
          '',
        )}
        ${
          user.id === TECHSUPPORT_ROOT_USER_ID
            ? ''
            : '<div id="settings-section-support-delegate" class="settings-section-wrapper"><div id="support-delegate-optin-section"></div></div>'
        }
        ${renderSettingsSection(
          {
            id: 'settings-section-erase-device',
            title: deps.t('settingsEraseDevice'),
            subtitle: deps.t('settingsEraseDeviceHelp'),
            action: `<button type="button" class="btn" id="settings-erase-device-btn" data-testid="settings-erase-device-btn" style="background:var(--danger);color:#fff;">${deps.t('settingsEraseDevice')}</button>`,
            danger: true,
          },
          '',
        )}
        ${renderSettingsSection(
          {
            id: 'settings-storage-inspector',
            title: deps.t('settingsStorage'),
            action: `<button type="button" class="btn" id="settings-refresh-storage-btn">${deps.t('settingsRefresh')}</button>`,
          },
          `<div id="settings-storage-inspector-body" style="font-size:0.9em;color:var(--text-tertiary);">${deps.t('settingsStorageLoading')}</div>`,
        )}
        ${renderSettingsSection(
          {
            id: 'settings-section-help',
            title: deps.t('settingsHelp'),
            subtitle: deps.t('settingsHelpSubtitle'),
            action: `<div class="settings-inline-actions"><button type="button" class="btn primary-btn" id="settings-start-guide-btn" data-testid="settings-start-guide-btn">${deps.t('settingsStartGuide')}</button><button type="button" class="btn" id="settings-replay-walkthrough-btn" data-testid="settings-replay-walkthrough-btn">${deps.t('settingsReplayWalkthrough')}</button></div>`,
          },
          `<div style="font-weight:600;margin-bottom:8px;">${deps.t('settingsHelpFaqHeading')}</div>
          ${renderTechSupportFaqHelpList(deps.getUiLanguage())}`,
        )}
        </div>
      </div>
    `;
  // TechSupport's support-inbox is an operator inbox, not a settings section — it stays
  // permanently visible above the menu/detail split rather than gated behind a menu tap.
  // docs/TODO.md K7: an eligible (non-master) user gets the opt-in prompt, plus the same
  // inbox section once they've actually opted in.
  if (user.id === TECHSUPPORT_ROOT_USER_ID) {
    container.insertAdjacentHTML(
      'afterbegin',
      '<div id="techsupport-recovery-banner-section"></div>' +
        '<div id="support-delegates-section" style="margin-bottom:14px;"></div>' +
        '<div id="support-inbox-section" style="margin-bottom:14px;"></div>',
    );
  } else {
    // Always rendered for a non-master user, eligible or not — support-delegate-optin-view.ts
    // itself branches between "enter an invite code" (not yet eligible) and the opt-in toggle
    // (holds a valid grant), so this container has somewhere to render either way.
    container.insertAdjacentHTML(
      'afterbegin',
      (deps.techSupportDelegateEligible && deps.techSupportDelegateOptedIn
        ? '<div id="support-inbox-section" style="margin-bottom:14px;"></div>'
        : ''),
    );
  }
  deps.bindSettingsControls();
  // These two sections do device/server inspection. Keep them lazy so simply opening Settings
  // cannot synchronously scan localStorage or launch manifest/network work.
  if (deps.settingsActiveSectionId === 'settings-storage-inspector') {
    void deps.refreshStorageInspector();
  }
  if (deps.settingsActiveSectionId === 'settings-section-download-app') {
    void deps.refreshDownloadAppSection();
  }
  deps.renderSupportInboxSectionIfPresent();
  deps.renderSupportDelegatesSectionIfPresent();
  deps.renderTechSupportRecoveryBannerIfPresent();
  deps.renderSupportDelegateOptInSectionIfPresent();
  deps.applySettingsSectionView(deps.settingsActiveSectionId);
}

/** Web/desktop: the self-defined encounter location replaces GPS for automatic Nearby. */
function renderEncounterLocationBlock(t: (key: UiTranslationKey) => string): string {
  const encounter = getEncounterLocation();
  // Never echo the coordinate here: Settings shows only that a point is set, and when.
  const statusText = encounter
    ? t('encounterLocationSet').replace('{date}', encounter.setAt ? new Date(encounter.setAt).toLocaleDateString() : '—')
    : t('encounterLocationUnset');
  return `<div data-testid="settings-encounter-location" style="padding:12px;border:1px solid var(--border-strong);border-radius:10px;background:var(--bg-subtle);margin-bottom:14px;">
            <strong>${t('encounterLocationTitle')}</strong>
            <div style="font-size:0.85em;margin:4px 0 8px;">${t('encounterLocationHelp')}</div>
            <div role="status" data-testid="settings-encounter-location-status" data-set="${encounter ? 'true' : 'false'}" style="font-size:0.85em;font-weight:600;margin-bottom:8px;">${escapeHtml(statusText)}</div>
            <div style="display:flex;gap:8px;flex-wrap:wrap;">
              <button type="button" class="btn primary-btn" id="settings-encounter-location-set" data-testid="settings-encounter-location-set">${t(encounter ? 'encounterLocationChangeButton' : 'encounterLocationSetButton')}</button>
              ${encounter ? `<button type="button" class="btn btn-secondary" id="settings-encounter-location-clear" data-testid="settings-encounter-location-clear">${t('encounterLocationClearButton')}</button>` : ''}
            </div>
          </div>`;
}

const PROVIDER_STATE_KEYS: Record<DiscoveryProviderAvailability, UiTranslationKey> = {
  on: 'settingsProviderOn',
  off: 'settingsProviderOff',
  unsupported: 'settingsProviderUnsupported',
  'permission-denied': 'settingsProviderPermissionDenied',
  'radio-off': 'settingsProviderRadioOff',
};

/** One discovery provider switch with its live availability (unsupported, permission, radio off). */
function renderNearbyProviderRow(
  kind: NearbyProviderSetting,
  titleKey: UiTranslationKey,
  helpKey: UiTranslationKey,
  t: (key: UiTranslationKey) => string,
): string {
  const enabled = getNearbyProviderEnabled(kind);
  const availability = discoveryProviderAvailability(kind, enabled, nearbyProviderEnvironment());
  const unsupported = availability === 'unsupported';
  const attention = availability === 'permission-denied' || availability === 'radio-off';
  return `<label style="display:flex;align-items:flex-start;gap:10px;cursor:${unsupported ? 'default' : 'pointer'};">
              <input type="checkbox" id="settings-nearby-${kind}" ${enabled && !unsupported ? 'checked' : ''} ${unsupported ? 'disabled' : ''}>
              <span><strong>${t(titleKey)}</strong>
                <small data-testid="settings-nearby-${kind}-state" data-availability="${availability}" style="margin-left:6px;${attention ? 'color:var(--warning-text);' : 'color:var(--text-tertiary);'}">${escapeHtml(t(PROVIDER_STATE_KEYS[availability]))}</small>
                <br><small>${t(helpKey)}</small></span>
            </label>`;
}

function nearbyProviderEnvironment(): DiscoveryProviderEnvironment {
  const win = window as unknown as {
    IinPublicNearby?: { startLanDiscovery?: unknown; nearbyReadiness?: () => string };
    __iinpublicNearbyOffline?: { getDiagnostics?: () => Record<string, unknown> };
  };
  let readiness: DiscoveryReadiness | null = null;
  try {
    readiness = win.IinPublicNearby?.nearbyReadiness ? JSON.parse(win.IinPublicNearby.nearbyReadiness()) as DiscoveryReadiness : null;
  } catch { readiness = null; }
  let permissionDenied = false;
  try { permissionDenied = win.__iinpublicNearbyOffline?.getDiagnostics?.().permissionDenied === true; } catch { /* service not started */ }
  return { nativeBridge: hasNearbyBridge(), readiness, permissionDenied };
}

/** The nearby switches only mean something inside the Android app (offline discovery bridge). */
function hasNearbyBridge(): boolean {
  if (typeof window === 'undefined') return false;
  const bridge = (window as unknown as { IinPublicNearby?: { startLanDiscovery?: unknown } }).IinPublicNearby;
  return typeof bridge?.startLanDiscovery === 'function';
}
